// client/src/components/audience/MessageComposer.jsx
//
// /:tenant/audience/messages/new[?segment=<id>] and
// /:tenant/audience/messages/:sendId/edit — write an email and/or push
// notification for one segment: merge fields ({{first_name|there}}),
// preview as a real contact, a test to yourself, then send now or schedule.
// Sending happens in the background (outreach-sender worker); this page then
// opens the send's report.
// Growth Phase 5, feature_key "audience_messaging".

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import {
  CHANNELS, createSend, errorText, getOptions, getSend, insertAt, isNotReady, listSegments, previewSend,
  queueSend, searchContacts, testSend, updateSend,
} from '../../services/audienceApi';
import { formatCount } from '../analytics/attributionFormat';
import { channelLabel } from './AudiencePage';
import '../analytics/PerformancePanel.css';
import '../forms/Forms.css';
import './Audience.css';

const BLANK = {
  name: '',
  channel: 'email',
  segmentId: '',
  requireOptIn: true,
  email: { subject: '', previewText: '', headline: '', body: '', ctaLabel: '', ctaUrl: '' },
  push: { title: '', body: '', url: '' },
};

/** The token the picker inserts: first name gets a friendly fallback. */
export function mergeToken(key) {
  return key === 'first_name' ? '{{first_name|there}}' : `{{${key}}}`;
}

/** datetime-local value → ISO string (local time), or null. */
export function scheduleIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function MessageComposer({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const tenant = Number(tenantProp || params.tenant);
  const [sendId, setSendId] = useState(params.sendId ? Number(params.sendId) : null);

  const [segments, setSegments] = useState(null);
  const [mergeFields, setMergeFields] = useState([]);
  const [draft, setDraft] = useState({ ...BLANK, segmentId: search.get('segment') || '' });
  const [status, setStatus] = useState('draft');
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState('');
  const [when, setWhen] = useState({ mode: 'now', at: '' });
  const [previewQuery, setPreviewQuery] = useState('');
  const [previewPeople, setPreviewPeople] = useState([]);
  const [previewContact, setPreviewContact] = useState('');
  const [preview, setPreview] = useState(null);
  const focused = useRef({ group: 'email', key: 'body', el: null });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [seg, opts] = await Promise.all([listSegments(tenant), getOptions(tenant)]);
        if (!live) return;
        setSegments(seg.data || []);
        setMergeFields(opts.data.mergeFields || []);
        if (params.sendId) {
          const res = await getSend(tenant, params.sendId);
          if (!live) return;
          const s = res.data;
          setStatus(s.status);
          setDraft({
            name: s.name, channel: s.channel, segmentId: s.segmentId || '', requireOptIn: s.requireOptIn,
            email: { ...BLANK.email, ...s.email }, push: { ...BLANK.push, ...s.push },
          });
        }
      } catch (err) {
        if (live) {
          setLoadError(isNotReady(err)
            ? t('audience.notReady', 'Audience messaging needs a database update before it can be used.')
            : t('audience.composer.loadError', 'Could not load this message.'));
        }
      }
    })();
    return () => { live = false; };
  }, [tenant, params.sendId, t]);

  const editable = status === 'draft' || status === 'scheduled';
  const showEmail = draft.channel === 'email' || draft.channel === 'both';
  const showPush = draft.channel === 'push' || draft.channel === 'both';
  const segment = (segments || []).find((s) => String(s.id) === String(draft.segmentId));

  const setPart = (group, key) => (e) => setDraft({ ...draft, [group]: { ...draft[group], [key]: e.target.value } });
  const onFocus = (group, key) => (e) => { focused.current = { group, key, el: e.target }; };

  const insertField = (key) => {
    const { group, key: field, el } = focused.current;
    const token = mergeToken(key);
    const current = draft[group][field] || '';
    const { text, caret } = insertAt(current, token, el?.selectionStart, el?.selectionEnd);
    setDraft({ ...draft, [group]: { ...draft[group], [field]: text } });
    if (el) requestAnimationFrame(() => { el.focus(); el.setSelectionRange?.(caret, caret); });
  };

  const body = () => ({
    name: draft.name.trim(),
    channel: draft.channel,
    segmentId: draft.segmentId ? Number(draft.segmentId) : null,
    requireOptIn: draft.requireOptIn,
    email: draft.email,
    push: draft.push,
  });

  /** Save (create or update); returns the send id. */
  const persist = async () => {
    if (sendId) {
      await updateSend(tenant, sendId, body());
      return sendId;
    }
    // The new id stays in state (the URL keeps /new) so a save followed by a
    // test or send doesn't remount the page and lose what's on screen.
    const res = await createSend(tenant, body());
    setSendId(res.data.id);
    return res.data.id;
  };

  const run = async (label, fn) => {
    setNotice(null);
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      setNotice({ kind: 'error', text: errorText(err, t('audience.composer.error', 'Something went wrong. Nothing was sent.')) });
    } finally {
      setBusy('');
    }
  };

  const saveDraft = (e) => {
    e?.preventDefault();
    return run('save', async () => {
      await persist();
      setNotice({ kind: 'ok', text: t('audience.composer.saved', 'Draft saved.') });
    });
  };

  const sendTest = () => run('test', async () => {
    const id = await persist();
    const res = await testSend(tenant, id);
    const parts = [];
    if (res.data.email) parts.push(t('audience.composer.testEmail', 'Email sent to {{to}}.', { to: res.data.email.sentTo }));
    if (res.data.push) {
      parts.push(res.data.push.devices
        ? t('audience.composer.testPush', 'Push sent to {{n}} of your devices.', { n: res.data.push.sent })
        : t('audience.composer.testPushNone', 'Turn on push notifications for your own account to test push.'));
    }
    setNotice({ kind: 'ok', text: parts.join(' ') });
  });

  const queue = () => run('queue', async () => {
    const at = when.mode === 'later' ? scheduleIso(when.at) : null;
    if (when.mode === 'later' && !at) throw Object.assign(new Error('when'), { response: { data: { error: t('audience.composer.pickTime', 'Pick a date and time.') } } });
    const id = await persist();
    await queueSend(tenant, id, at);
    navigate(`/${tenant}/audience/messages/${id}`);
  });

  const findPeople = () => run('people', async () => {
    const res = await searchContacts(tenant, { q: previewQuery, segmentId: draft.segmentId || undefined });
    setPreviewPeople(res.data || []);
    if (!res.data?.length) setNotice({ kind: 'info', text: t('audience.composer.noPeople', 'No one in this segment matches. Showing a sample person.') });
  });

  const showPreview = () => run('preview', async () => {
    const res = await previewSend(tenant, {
      email: draft.email, push: draft.push, contactId: previewContact ? Number(previewContact) : undefined,
    });
    setPreview(res.data);
  });

  if (loadError) return <div className="au-page"><div className="note error" role="alert">{loadError}</div></div>;
  if (!segments) return <div className="au-page"><LoadingState /></div>;

  return (
    <div className="au-page">
      <section className="surface">
        <h2>{sendId ? t('audience.composer.editTitle', 'Edit message') : t('audience.composer.newTitle', 'New message')}</h2>
        <p className="surface-muted perf-small">
          {t('audience.composer.intro', 'Email goes only to people who have not unsubscribed. Push goes only to people who turned on notifications. Links to your On-Screen pages are tracked so the report can show clicks and sales.')}
        </p>
      </section>

      {!editable && (
        <div className="note info">
          {t('audience.composer.locked', 'This message is already {{status}} and can no longer be edited.', { status })}{' '}
          <button type="button" className="lm-btn secondary" onClick={() => navigate(`/${tenant}/audience/messages/${sendId}`)}>
            {t('audience.sends.report', 'Report')}
          </button>
        </div>
      )}

      <form className="surface" onSubmit={saveDraft}>
        <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 10 }}>
          <div className="fo-form-grid">
            <label className="fo-field">
              {t('audience.composer.name', 'Name (staff only)')}
              <input value={draft.name} required maxLength={160} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={t('audience.composer.namePlaceholder', 'Gala reminder, week before')} />
            </label>
            <label className="fo-field">
              {t('audience.composer.segment', 'Send to')}
              <select value={draft.segmentId} required onChange={(e) => setDraft({ ...draft, segmentId: e.target.value })}>
                <option value="">{t('audience.composer.pickSegment', 'Choose a segment')}</option>
                {segments.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.cachedCount !== null ? ` (${formatCount(s.cachedCount)})` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="au-channel" role="radiogroup" aria-label={t('audience.composer.channel', 'Channel')}>
            {CHANNELS.map((c) => (
              <label key={c} className={draft.channel === c ? 'on' : ''}>
                <input type="radio" name="channel" value={c} checked={draft.channel === c}
                  onChange={() => setDraft({ ...draft, channel: c })} />
                {channelLabel(t, c)}
              </label>
            ))}
          </div>

          {showEmail && (
            <label className="fo-check">
              <input type="checkbox" checked={draft.requireOptIn} onChange={(e) => setDraft({ ...draft, requireOptIn: e.target.checked })} />
              {t('audience.composer.requireOptIn', 'Email only people who opted in to email (recommended)')}
            </label>
          )}

          <div className="au-merge">
            <span>{t('audience.composer.insertField', 'Insert a field:')}</span>
            {mergeFields.map((f) => (
              <button key={f.key} type="button" className="au-chip" onMouseDown={(e) => e.preventDefault()} onClick={() => insertField(f.key)}>
                {t(`audience.merge.${f.key}`, f.label)}
              </button>
            ))}
          </div>

          {showEmail && (
            <fieldset className="fo-preview" style={{ display: 'grid', gap: 8 }}>
              <legend className="perf-small">{t('audience.composer.emailLegend', 'Email')}</legend>
              <label className="fo-field">
                {t('audience.composer.subject', 'Subject')}
                <input value={draft.email.subject} maxLength={255} required onFocus={onFocus('email', 'subject')} onChange={setPart('email', 'subject')} />
              </label>
              <label className="fo-field">
                {t('audience.composer.previewText', 'Preview text (shown after the subject in the inbox)')}
                <input value={draft.email.previewText} maxLength={255} onFocus={onFocus('email', 'previewText')} onChange={setPart('email', 'previewText')} />
              </label>
              <label className="fo-field">
                {t('audience.composer.headline', 'Headline')}
                <input value={draft.email.headline} maxLength={255} onFocus={onFocus('email', 'headline')} onChange={setPart('email', 'headline')} />
              </label>
              <label className="fo-field">
                {t('audience.composer.body', 'Message (a blank line starts a new paragraph)')}
                <textarea className="au-textarea" value={draft.email.body} required onFocus={onFocus('email', 'body')} onChange={setPart('email', 'body')} />
              </label>
              <div className="fo-form-grid">
                <label className="fo-field">
                  {t('audience.composer.ctaLabel', 'Button text')}
                  <input value={draft.email.ctaLabel} maxLength={120} onChange={setPart('email', 'ctaLabel')} />
                </label>
                <label className="fo-field">
                  {t('audience.composer.ctaUrl', 'Button link')}
                  <input type="url" value={draft.email.ctaUrl} maxLength={500} placeholder="https://" onChange={setPart('email', 'ctaUrl')} />
                </label>
              </div>
            </fieldset>
          )}

          {showPush && (
            <fieldset className="fo-preview" style={{ display: 'grid', gap: 8 }}>
              <legend className="perf-small">{t('audience.composer.pushLegend', 'Push notification')}</legend>
              <label className="fo-field">
                {t('audience.composer.pushTitle', 'Title')}
                <input value={draft.push.title} maxLength={120} required onFocus={onFocus('push', 'title')} onChange={setPart('push', 'title')} />
              </label>
              <label className="fo-field">
                {t('audience.composer.pushBody', 'Text')}
                <input value={draft.push.body} maxLength={300} required onFocus={onFocus('push', 'body')} onChange={setPart('push', 'body')} />
              </label>
              <label className="fo-field">
                {t('audience.composer.pushUrl', 'Opens (link)')}
                <input type="url" value={draft.push.url} maxLength={500} placeholder="https://" onChange={setPart('push', 'url')} />
              </label>
            </fieldset>
          )}

          <fieldset className="fo-preview" style={{ display: 'grid', gap: 8 }}>
            <legend className="perf-small">{t('audience.composer.whenLegend', 'When')}</legend>
            <div className="au-actions">
              <label className="fo-check">
                <input type="radio" name="when" checked={when.mode === 'now'} onChange={() => setWhen({ ...when, mode: 'now' })} />
                {t('audience.composer.now', 'Send now')}
              </label>
              <label className="fo-check">
                <input type="radio" name="when" checked={when.mode === 'later'} onChange={() => setWhen({ ...when, mode: 'later' })} />
                {t('audience.composer.later', 'Schedule')}
              </label>
              {when.mode === 'later' && (
                <label className="fo-field">
                  <span className="visually-hidden">{t('audience.composer.at', 'Date and time')}</span>
                  <input type="datetime-local" aria-label={t('audience.composer.at', 'Date and time')} value={when.at}
                    onChange={(e) => setWhen({ ...when, at: e.target.value })} />
                </label>
              )}
            </div>
            <p className="surface-muted perf-small">
              {t('audience.composer.whenHelp', 'Messages wait out your quiet hours, and people who already got today\'s limit of messages from you are skipped.')}
            </p>
          </fieldset>

          <div className="au-actions">
            <button type="submit" className="lm-btn secondary" disabled={!!busy || !draft.name.trim()}>
              {busy === 'save' ? t('audience.saving', 'Saving…') : t('audience.composer.save', 'Save draft')}
            </button>
            <button type="button" className="lm-btn secondary" disabled={!!busy || !draft.name.trim()} onClick={sendTest}>
              {busy === 'test' ? t('audience.composer.testing', 'Sending test…') : t('audience.composer.test', 'Send a test to me')}
            </button>
            <button type="button" className="lm-btn" disabled={!!busy || !draft.name.trim() || !draft.segmentId} onClick={queue}>
              {when.mode === 'later'
                ? t('audience.composer.schedule', 'Schedule')
                : t('audience.composer.send', 'Send to {{n}} people', { n: segment?.cachedCount === null || !segment ? '…' : formatCount(segment.cachedCount) })}
            </button>
          </div>
        </fieldset>
        {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
      </form>

      <section className="surface">
        <h4>{t('audience.composer.previewTitle', 'Preview as a contact')}</h4>
        <div className="fo-inline">
          <label className="fo-field">
            {t('audience.composer.findPerson', 'Find someone in the segment')}
            <input value={previewQuery} onChange={(e) => setPreviewQuery(e.target.value)} placeholder={t('audience.composer.findPlaceholder', 'Name or email')} />
          </label>
          <button type="button" className="lm-btn secondary" onClick={findPeople} disabled={!!busy}>{t('audience.composer.find', 'Find')}</button>
          <label className="fo-field">
            {t('audience.composer.previewAs', 'Preview as')}
            <select value={previewContact} onChange={(e) => setPreviewContact(e.target.value)}>
              <option value="">{t('audience.composer.sample', 'A sample person (no name)')}</option>
              {previewPeople.map((p) => (
                <option key={p.id} value={p.id}>{[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email} ({p.email})</option>
              ))}
            </select>
          </label>
          <button type="button" className="lm-btn" onClick={showPreview} disabled={!!busy}>{t('audience.composer.showPreview', 'Show preview')}</button>
        </div>
        {preview && (
          <div className="au-split" style={{ marginTop: 10 }}>
            {preview.missingFields?.length > 0 && (
              <div className="note warn" style={{ gridColumn: '1 / -1' }}>
                {t('audience.composer.missing', 'This person has no {{fields}}: the fallback text is used.', { fields: preview.missingFields.join(', ') })}
              </div>
            )}
            {showEmail && (
              <div>
                <p className="perf-small"><strong>{t('audience.composer.subject', 'Subject')}:</strong> {preview.email.subject}</p>
                <iframe
                  className="au-preview-frame"
                  title={t('audience.composer.emailPreview', 'Email preview')}
                  sandbox=""
                  srcDoc={preview.email.html}
                />
              </div>
            )}
            {showPush && (
              <div className="au-push" aria-label={t('audience.composer.pushPreview', 'Push preview')}>
                <strong>{preview.push.title}</strong>
                <span>{preview.push.body}</span>
                {preview.push.url && <small>{preview.push.url}</small>}
              </div>
            )}
          </div>
        )}
      </section>

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(`/${tenant}/audience`) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
